import axios from 'axios';
import { SearchFilters, SearchResponse } from '../types';
import { identifyUser } from '../utils/mixpanel';

// Get API URL from window.env (loaded from external env.js) or fallback to process.env or default
const API_BASE_URL =
  (window.env?.REACT_APP_API_URL) ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:4000/api';

const DOMAINS_API_BASE_URL =
  (window.env?.REACT_APP_DOMAINS_API_URL) ||
  process.env.REACT_APP_DOMAINS_API_URL ||
  API_BASE_URL;

const DOMAINS_API_TOKEN =
  (window.env?.REACT_APP_DOMAINS_API_TOKEN) ||
  process.env.REACT_APP_DOMAINS_API_TOKEN ||
  '';

const TOKEN_STORAGE_KEY = 'auth_token';

export interface AuthResponse {
  accessToken: string;
  user: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
    role: 'user' | 'admin' | 'superadmin';
    allowedTenants: { id: number; name: string }[];
  };
}

const _storedTid = localStorage.getItem('tid');
let _activeTenantId: number | null = _storedTid ? Number(_storedTid) : null;

export function setActiveTenantId(id: number | null) {
  _activeTenantId = id;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include Authorization header from stored JWT (localStorage = shared across tabs)
api.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (token) {
        // Axios types expect AxiosHeaders; cast for compatibility
        if (!config.headers) {
          config.headers = {} as any;
        }
        (config.headers as any).Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // If anything goes wrong reading storage, clear token
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.request.use((config) => {
  const url = config.url || '';
  // Profile and other user-management calls are not tenant-scoped; attaching `tid`
  // triggers TenantContextInterceptor and can 403 (e.g. admin allowedDomains mismatch).
  // Per-request opt-out via `__skipTid` lets admin flows view profiles across tenants.
  const skipTid =
    url.startsWith('/admin') ||
    url.startsWith('/user-management') ||
    (config as any).__skipTid === true;
  if (_activeTenantId !== null && !skipTid) {
    config.params = { ...config.params, tid: _activeTenantId };
  }
  return config;
});

// Domains API — separate base URL, same auth
const domainsApi = axios.create({
  baseURL: DOMAINS_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

domainsApi.interceptors.request.use((config) => {
  const token = DOMAINS_API_TOKEN || localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    if (!config.headers) config.headers = {} as any;
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global 401 handler: clear auth and redirect to login (skip auth endpoints that return 401 as normal flow)
const AUTH_ENDPOINTS = ['/user-management/login', '/user-management/check-email', '/auth/magic-link/verify'];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const requestUrl = error?.config?.url || '';
      const isAuthEndpoint = AUTH_ENDPOINTS.some((ep) => requestUrl.includes(ep));
      if (!isAuthEndpoint) {
        try {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem('current_user');
          localStorage.removeItem('tid');
          localStorage.removeItem('own_tid');
          _activeTenantId = null;
        } catch {
          // ignore storage errors
        }
        if (typeof window !== 'undefined') {
          const redirect = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = redirect ? `/login?redirect=${redirect}` : '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ─── Talent Search sessions (Add Candidates > Search tab) ────────────────────
export interface SearchSessionQuery {
  queryText?: string;
  filters?: Record<string, any>;
  decomposition?: Record<string, any>;
}

export interface SearchSession {
  id: number;
  title: string;
  jobId: number | null;
  updatedAt: string;
  queryCount: number;
  filterCount: number;
  cartCount: number;
  latestQuery?: SearchSessionQuery | null;
}

export interface SearchSessionsResponse {
  items: SearchSession[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export const getSearchSessions = async (
  page: number = 1,
  pageSize: number = 4,
): Promise<SearchSessionsResponse> => {
  const response = await api.get<SearchSessionsResponse>('/admin/pipeline/search-sessions', {
    params: { page, pageSize },
  });
  return response.data;
};

export interface SearchBatch {
  id: number;
  batchName: string;
  displayName: string;
  profileCount: number;
  sentAt: string | null;
  createdAt: string;
  domainCounts: Record<string, number>;
}

export interface SearchBatchesResponse {
  batches: SearchBatch[];
  totalUniqueCount: number;
  domainCounts: Record<string, number>;
}

export const listSearchBatches = async (): Promise<SearchBatchesResponse> => {
  const response = await api.get<SearchBatchesResponse>('/swe-search/batches');
  return response.data;
};

export const searchEngineers = async (filters: SearchFilters): Promise<SearchResponse> => {
  // Backend /find expects "query" from top search bar; map searchQuery -> query and omit searchQuery from payload
  const payload = { ...filters } as Record<string, unknown>;
  payload.query = filters.searchQuery?.trim() || undefined;
  delete payload.searchQuery;
  // Backend /find requires domain; default to SWE
  payload.domain = filters.domain || 'SWE';
  const response = await api.post<SearchResponse>('/swe-search/find', payload);
  return response.data;
};

/* Share links (?ref=<code> from the create-account wizard) */

const SHARE_REF_KEY = 'share_ref';

// Called on app boot: remember the code and count the click once per browser session.
export const captureShareRef = async (): Promise<void> => {
  const code = new URLSearchParams(window.location.search).get('ref');
  if (!code) return;
  localStorage.setItem(SHARE_REF_KEY, code);
  if (sessionStorage.getItem(SHARE_REF_KEY) === code) return;
  sessionStorage.setItem(SHARE_REF_KEY, code);
  try {
    await api.post(`/share/${encodeURIComponent(code)}/click`);
  } catch {
    // click counting is best-effort
  }
};

// Called after login/magic-link verify: stamps referred_by_code on the user (first touch wins).
const attributeShareRef = async (): Promise<void> => {
  const code = localStorage.getItem(SHARE_REF_KEY);
  if (!code) return;
  // Consume it: a leftover code would otherwise attribute every later login on this browser.
  localStorage.removeItem(SHARE_REF_KEY);
  try {
    await api.post('/share/attribute', { code });
  } catch {
    // attribution is best-effort
  }
};

export const checkEmail = async (email: string): Promise<{ hasPassword: boolean }> => {
  const response = await api.post<{ hasPassword: boolean }>('/user-management/check-email', { email });
  return response.data;
};

export const login = async (email: string, password: string): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>('/user-management/login', {
    email,
    password,
  });

  const { accessToken, user } = response.data;

  if (accessToken) {
    localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
  }

  if (user) {
    localStorage.setItem('current_user', JSON.stringify(user));
    if (user.allowedTenants?.[0]?.id != null) {
      localStorage.setItem('own_tid', String(user.allowedTenants[0].id));
      localStorage.setItem('tid', String(user.allowedTenants[0].id));
      setActiveTenantId(user.allowedTenants[0].id);
    } else {
      localStorage.removeItem('tid');
      localStorage.removeItem('own_tid');
      setActiveTenantId(null);
    }
    identifyUser(user);
    await attributeShareRef();
  }

  return response.data;
};

export const sendMagicLink = async (email: string, redirect?: string): Promise<{ message: string }> => {
  const response = await api.post<{ message: string }>('/auth/magic-link/send', { email, ...(redirect && redirect !== '/' ? { redirect } : {}) });
  return response.data;
};

export const verifyMagicLink = async (token: string): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>('/auth/magic-link/verify', { token });

  const { accessToken, user } = response.data;

  if (accessToken) {
    localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
  }

  if (user) {
    localStorage.setItem('current_user', JSON.stringify(user));
    if (user.allowedTenants?.[0]?.id != null) {
      localStorage.setItem('own_tid', String(user.allowedTenants[0].id));
      localStorage.setItem('tid', String(user.allowedTenants[0].id));
      setActiveTenantId(user.allowedTenants[0].id);
    } else {
      localStorage.removeItem('tid');
      localStorage.removeItem('own_tid');
      setActiveTenantId(null);
    }
    await attributeShareRef();
  }

  return response.data;
};

export interface UpdateProfileResponse {
  user: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    role: 'user' | 'admin' | 'superadmin';
    allowedTenants: { id: number; name: string }[];
  };
}

export const updateProfile = async (
  firstName: string,
  lastName: string,
): Promise<UpdateProfileResponse> => {
  const response = await api.patch<UpdateProfileResponse>('/user-management/profile', {
    firstName,
    lastName,
  });

  if (response.data.user) {
    const existing = localStorage.getItem('current_user');
    const current = existing ? JSON.parse(existing) : {};
    localStorage.setItem(
      'current_user',
      JSON.stringify({ ...current, ...response.data.user }),
    );
  }

  return response.data;
};

export interface DeveloperDetails {
  id: number;
  name?: string | null;
  city?: string | null;
  country?: string | null;
  yearOfExperience?: number | null;
  title?: string | null;
  availability?: string | null;
  linkedInProfile?: string | null;
  linkedInVerify?: number | null; // 1 or 0
  linkedInVerification?: {
    joinedDate?: string | null;
    Identity?: Array<{ Value?: string; Date?: string }> | null;
    Workplace?: Array<{ Value?: string; Date?: string }> | null;
    EducationalInstitution?: Array<{ Value?: string; Date?: string }> | null;
  } | null;
  score?: number | null;
  tSummary?: string[] | null;
  resumeLink?: string | null;
  resumeText?: string | null;
  tSkills?: string[] | null;
  workExperience?: Array<{
    position?: string | null;
    company?: string | null;
    startMonth?: number | null;
    startYear?: number | null;
    endYear?: number | null;
    endMonth?: number | null;
    description?: string[] | null;
  }> | null;
  education?: Array<{
    level?: string | null;
    degree?: string | null;
    school?: string | null;
    startYear?: number | null;
    endYear?: number | null;
  }> | null;
  projectDetails?: Array<{
    title?: string | null;
    description?: string | null;
    startYear?: number | null;
    endYear?: number | null;
    url?: string | null;
    skills?: string[] | null;
  }> | null;
  hobbies?: string[] | null;
  skills?: Array<{
    name?: string | null;
    yearsOfExperience?: number | null;
  }> | null;
  tags?: string[] | null;
  codingChallengeDetails?: {
    submitId?: number | null;
    title?: string | null;
    score?: number | null;
    total?: number | null;
    date?: string | null;
    recordingURL?: string | null;
    tSummaryAndPerformance?: string | null;
    programmingLanguage?: string | null;
  } | null;
  /** Key takeaways from the coding challenge */
  codingChallengeTakeaway?: string[] | null;
  aiInterviewDetails?: {
    score?: number | null;
    date?: string | null;
    /** True when a recording exists; stream via getAiInterviewStreamUrl */
    hasRecording?: boolean;
    recordingURL?: string | null;
    transcript?:
      | {
          id: 'interviewer' | 'interviewee';
          content: string;
        }[]
      | null;
    interviewSummary?: Array<{
      reason?: string | null;
      description?: string | null;
    }> | null;
    takeaways?: string[] | null;
  } | null;
  languages?: {
    natural?: string[] | null;
    programming?: string[] | null;
  } | null;
  /** Impact metrics (e.g. cost savings, context for peak moment) */
  impactMetrics?: {
    label?: string | null;
    value?: string | number | null;
    unit?: string | null;
    confidence?: number | null;
    context?: string | null;
  } | null;
  /** Bay Area talent signals; evidence + proofPoints/badges can be used for peak moment */
  bayAreaTalentSignals?: {
    evidence?: {
      impactMetrics?: string[] | null;
      notableCompanies?: string[] | null;
      topSchools?: Array<{ name?: string | null; abbrev?: string | null }> | null;
    } | null;
    /** Sentence-style proof points; good fallback for peak moment */
    proofPoints?: string[] | null;
    /** Short badges (e.g. "Hackathon Winner"); last-resort fallback for peak moment */
    badges?: string[] | null;
  } | null;
  /** Pedigree signals: notable companies and single top school used as highlights */
  pedigreeSignals?: {
    notableCompanies?: string[] | null;
    topSchool?: string | null;
  } | null;
  researchPapers?: Array<{
    title?: string | null;
    year?: string | number | null;
    url?: string | null;
    authors?: string[] | null;
  }> | null;
  /** Google Scholar data when verified */
  scholarVerify?: {
    url?: string | null;
    citations?: number | null;
    hIndex?: number | null;
    i10Index?: number | null;
    publications?: Array<{
      title?: string | null;
      link?: string | null;
      authors?: string | null;
      citations?: number | null;
      year?: number | null;
    }> | null;
  } | null;
  /** GitHub data */
  githubData?: {
    contributions?: number | null;
    repos?: number | null;
    stars?: number | null;
  } | null;
  /** Type of work / Expertise (e.g., RL GYM, MLE bench, etc.) */
  typeOfWork?: string | null;
  /** Field (STEM profiles, e.g. Physics, Chemistry) */
  field?: string | null;
  /** Certifications */
  certificates?: Array<{
    name?: string | null;
    year?: number | null;
    certOrg?: string | null;
  }> | null;
  /** CFA charterholder flag */
  isCFA?: boolean;
  /** Google Scholar profile URL (set by backend from raw field) */
  googleScholarURL?: string | null;
  /** Additional links (teaching/research pages, portfolios, etc.) */
  additionalLinks?: string[] | null;
  /** Teaching or research profile link with verification status */
  teachingOrResearchProfile?: {
    link?: string | null;
    isVerified?: boolean;
    hidden?: boolean;
    linkCheckReason?: string;
  } | null;
  /** Additional verifications from automated cross-referencing */
  additionalVerifications?: Array<{
    text: string;
    link?: string | null;
  }> | null;
  /** Last update date from dv2_developer_availability */
  lastContactDate?: string | null;
  /** User action flags from backend */
  isViewed?: boolean;
  viewedAt?: string | null;
  isSaved?: boolean;
  isDiscarded?: boolean;
}

export const getDeveloperDetails = async (
  id: string,
  domain?: string,
  opts?: { bypassTenant?: boolean },
): Promise<DeveloperDetails> => {
  const params = domain ? { domain } : {};
  const config: any = { params };
  if (opts?.bypassTenant) config.__skipTid = true;
  const response = await api.get<DeveloperDetails>(`/developers/${id}`, config);
  return response.data;
};

export interface RecordingUrlResponse {
  recordingURL: string;
}

export const getRecordingUrl = async (
  developerId: string,
  submitId: number,
): Promise<RecordingUrlResponse> => {
  const response = await api.get<RecordingUrlResponse>(
    `/developers/${developerId}/recording-url`,
    { params: { submitId } },
  );
  return response.data;
};

export const getAiInterviewStreamUrl = (developerId: string, domain: DomainType): string | null => {
  const token = localStorage.getItem('auth_token');
  if (!token) return null;
  return `${API_BASE_URL}/developers/${encodeURIComponent(developerId)}/ai-interview-recording-stream?domain=${encodeURIComponent(domain)}&authorization=${encodeURIComponent(token)}`;
};

// ==================== Search Actions API ====================

export type ActionType = 'SAVED' | 'VIEWED' | 'DISCARDED';
export type DomainType = string;

export interface ActionResponse {
  success: boolean;
  message: string;
}

/**
 * Mark a talent as viewed
 */
export const viewTalent = async (
  talentId: number,
  domain: DomainType = 'SWE',
): Promise<ActionResponse> => {
  const response = await api.post<ActionResponse>('/search-actions/view', {
    talent_id: talentId,
    domain,
  });
  return response.data;
};

/**
 * Save a talent to the shortlist
 */
export const saveTalent = async (
  talentId: number,
  domain: DomainType = 'SWE',
): Promise<ActionResponse> => {
  const response = await api.post<ActionResponse>('/search-actions/save', {
    talent_id: talentId,
    domain,
  });
  return response.data;
};

/**
 * Discard a talent (remove from saved and viewed)
 */
export const discardTalent = async (
  talentId: number,
  domain: DomainType = 'SWE',
): Promise<ActionResponse> => {
  const response = await api.post<ActionResponse>('/search-actions/discard', {
    talent_id: talentId,
    domain,
  });
  return response.data;
};

/**
 * Restore a previously discarded talent
 */
export const restoreTalent = async (
  talentId: number,
  domain: DomainType = 'SWE',
): Promise<ActionResponse> => {
  const response = await api.post<ActionResponse>('/search-actions/restore', {
    talent_id: talentId,
    domain,
  });
  return response.data;
};

/**
 * Unsave a previously saved talent (remove from saved list)
 */
export const unsaveTalent = async (
  talentId: number,
  domain: DomainType = 'SWE',
): Promise<ActionResponse> => {
  const response = await api.post<ActionResponse>('/search-actions/unsave', {
    talent_id: talentId,
    domain,
  });
  return response.data;
};

/**
 * Get talents by action type (SAVED, VIEWED, or DISCARDED)
 */
export const getTalents = async (
  actionType: ActionType,
  domain: DomainType = 'SWE',
  page: number = 1,
  pageSize: number = 20,
): Promise<SearchResponse> => {
  const response = await api.post<SearchResponse>('/search-actions/talents', {
    type: actionType,
    domain,
    page,
    pageSize,
  });
  return response.data;
};

export const getTalentsBatch = async (
  actionType: ActionType,
  domains: DomainType[],
  page: number = 1,
  pageSize: number = 20,
): Promise<{ results: Record<string, SearchResponse> }> => {
  const response = await api.post<{ results: Record<string, SearchResponse> }>(
    '/search-actions/talents-batch',
    { type: actionType, domains, page, pageSize },
  );
  return response.data;
};

export const getAdminTenants = async (): Promise<{ id: number; name: string; domain: string }[]> => {
  const response = await api.get<{ id: number; name: string; domain: string }[]>('/admin/tenants');
  return response.data;
};

export const getShareLink = async (): Promise<{ shareId: string }> => {
  const response = await api.get<{ shareId: string }>('/search-actions/share-link');
  return response.data;
};

export interface SharedListDomain {
  domain: string;
  label: string;
  engineers: any[];
}

export interface SharedListResponse {
  ownerName: string;
  lastUpdated: string | null;
  domains: SharedListDomain[];
}

export const getSharedList = async (shareId: string): Promise<SharedListResponse> => {
  const response = await api.get<SharedListResponse>(`/shared-list/${shareId}`);
  return response.data;
};

// ==================== Pipeline API ====================

export interface CsvColumnSpec {
  name: string;
  required: boolean;
  description: string;
}

export interface DomainCsvFormat {
  domain: string;
  label: string;
  filename: string;
  columns: CsvColumnSpec[];
}

export interface ExistingTalent {
  talentId: string;
  name: string;
  domain: string;
  status: string;
}

export interface CsvUploadResult {
  success: boolean;
  domain: string;
  filename: string;
  size: number;
  valid: boolean;
  errors: string[];
  warnings?: string[];
  rowCount: number;
  uniqueTalentCount?: number;
  headers: string[];
  existingTalents?: ExistingTalent[];
  newTalents?: ExistingTalent[];
}

export interface PipelineRunStart {
  runId: string;
  skipped?: boolean;
  status: string;
  domains: string[];
  phases: number[];
  startedAt: string;
  totalTalents: number;
  cachedTalents: number;
  newTalents: number;
}

export interface PipelineRunStatus {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'preview_ready' | 'rejected' | 'queued';
  currentStep: string | null;
  // UI step ids skipped this run (e.g. local fraud steps when Matching fully covered the batch).
  skippedSteps?: string[];
  totalTalents: number;
  domain: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface PipelineRunSummary {
  id: string;
  adminId: number;
  adminEmail: string | null;
  status: string;
  domain: string | null;
  totalTalents: number;
  error: string | null;
  groupName: string | null;
  hasInputData: boolean;
  hasOutputData: boolean;
  currentStep: string | null;
  startedAt: string;
  finishedAt: string | null;
  fromSearchPortal: boolean;
  groupCounts: { accepted: number; inReview: number; rejected: number } | null;
}

export interface CsvPreviewData {
  columns: string[];
  rows: Record<string, unknown>[];
  totalRows: number;
}

export interface StepPrompt {
  name: string;
  role: 'system' | 'user';
  domain: string | null;
  text: string;
}

export interface StepDefinition {
  id: string;
  label: string;
  description: string;
  detailedDescription: string;
  domains: string[] | null;
  prompts: StepPrompt[];
}

export const getPipelineStepDefinitions = async (domain?: string): Promise<StepDefinition[]> => {
  const params = domain ? { domain } : {};
  const response = await api.get<StepDefinition[]>('/admin/pipeline/step-definitions', { params });
  return response.data;
};

export const getPipelineCsvFormats = async (): Promise<DomainCsvFormat[]> => {
  const response = await api.get<DomainCsvFormat[]>('/admin/pipeline/csv-formats');
  return response.data;
};

export const uploadPipelineCsv = async (
  domain: string,
  file: File,
): Promise<CsvUploadResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<CsvUploadResult>(
    `/admin/pipeline/upload/${domain}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

export const validatePipelineCsv = async (
  domain: string,
  file: File,
): Promise<CsvUploadResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<CsvUploadResult>(
    `/admin/pipeline/validate/${domain}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

export interface DeepValidationCheck {
  column: string;
  status: 'ok' | 'warning' | 'error';
  message: string;
}

export interface DeepValidationRow {
  row: number;
  talentId: string;
  checks: DeepValidationCheck[];
}

export interface DeepValidationEvent {
  checked: number;
  total: number;
  rows: DeepValidationRow[];
  done: boolean;
  summary?: { totalRows: number; passed: number; warnings: number; errors: number };
  error?: string;
}

export interface DeepValidationStartResult {
  success: boolean;
  validationId?: string;
  totalRows?: number;
  errors?: string[];
}

export const startDeepValidation = async (
  domain: string,
  file: File,
): Promise<DeepValidationStartResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<DeepValidationStartResult>(
    `/admin/pipeline/deep-validate/${domain}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

export function createDeepValidationStream(validationId: string): EventSource {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  let url = `${base}/api/admin/pipeline/deep-validate/${validationId}/stream`;
  if (token) url += `?authorization=${encodeURIComponent(token)}`;
  return new EventSource(url);
}

export const startPipelineRun = async (config: {
  domains: string[];
  forceFresh?: boolean;
  preview?: boolean;
  uploadGroup?: string;
  skipTalentIds?: string[];
  file: File;
}): Promise<PipelineRunStart> => {
  const { file, ...jsonFields } = config;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('config', JSON.stringify(jsonFields));
  const response = await api.post<PipelineRunStart>('/admin/pipeline/run', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
  });
  return response.data;
};

export const getPipelineRunStatus = async (runId: string): Promise<PipelineRunStatus> => {
  const response = await api.get<PipelineRunStatus>(`/admin/pipeline/runs/${runId}`);
  return response.data;
};

export const listPipelineRuns = async (): Promise<PipelineRunSummary[]> => {
  const response = await api.get<PipelineRunSummary[]>('/admin/pipeline/runs');
  return response.data;
};

export const stopPipelineRun = async (runId: string): Promise<{ runId: string; status: string }> => {
  const response = await api.post<{ runId: string; status: string }>(`/admin/pipeline/runs/${runId}/stop`);
  return response.data;
};

export const approvePipelineRun = async (runId: string): Promise<{ runId: string; status: string }> => {
  const response = await api.post<{ runId: string; status: string }>(`/admin/pipeline/runs/${runId}/approve`);
  return response.data;
};

export const rejectPipelineRun = async (runId: string): Promise<{ runId: string; status: string }> => {
  const response = await api.post<{ runId: string; status: string }>(`/admin/pipeline/runs/${runId}/reject`);
  return response.data;
};

export const getPipelineInputPreview = async (runId: string, limit = 20): Promise<CsvPreviewData> => {
  const response = await api.get<CsvPreviewData>(`/admin/pipeline/runs/${runId}/input-preview`, { params: { limit } });
  return response.data;
};

export const getPipelineOutputPreview = async (runId: string, limit = 20): Promise<CsvPreviewData> => {
  const response = await api.get<CsvPreviewData>(`/admin/pipeline/runs/${runId}/output-preview`, { params: { limit } });
  return response.data;
};

export interface PipelineResults {
  columns: string[];
  rows: Record<string, unknown>[];
}

export const fetchPipelineResults = async (runId: string): Promise<PipelineResults> => {
  const response = await api.get<PipelineResults>(`/admin/pipeline/runs/${runId}/results`);
  return response.data;
};

export const downloadPipelineResult = (runId: string): void => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  let url = `${base}/api/admin/pipeline/runs/${runId}/download`;
  if (token) url += `?authorization=${encodeURIComponent(token)}`;
  window.open(url, '_blank');
};

export const downloadPipelineInput = (runId: string): void => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  let url = `${base}/api/admin/pipeline/runs/${runId}/download-input`;
  if (token) url += `?authorization=${encodeURIComponent(token)}`;
  window.open(url, '_blank');
};

// ==================== Data Ingestion API ====================

export interface IngestionValidationResult {
  valid: boolean;
  domain: string;
  filename: string;
  rowCount: number;
  headers: string[];
  errors: string[];
  warnings: string[];
}

export interface IngestionResult {
  success: boolean;
  step: 'validation' | 'generation' | 'complete';
  domain: string;
  filename: string;
  indexName?: string;
  rowCount: number;
  profilesGenerated?: number;
  indexed?: number;
  failed?: number;
  errors?: string[];
  warnings?: string[];
  message: string;
}

export const validateIngestionFile = async (
  domain: string,
  file: File,
): Promise<IngestionValidationResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<IngestionValidationResult>(
    `/admin/pipeline/ingest/${domain}/validate`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

export const ingestFile = async (
  domain: string,
  file: File,
  indexName?: string,
): Promise<IngestionResult> => {
  const formData = new FormData();
  formData.append('file', file);
  if (indexName) formData.append('indexName', indexName);
  const response = await api.post<IngestionResult>(
    `/admin/pipeline/ingest/${domain}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 },
  );
  return response.data;
};


// ==================== Domain Config API ====================

export interface WhitelistedDomain {
  id: number;
  domain: string;
  name: string | null;
  isActive: boolean;
  config: Record<string, unknown> | null;
  includeGoldenDataset: boolean;
  updatedBy: string | null;
  addedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountPayload {
  name: string;
  includeGoldenDataset: boolean;
  enabledSearchDomainKeys: string[];
  managerUserIds: number[];
}

export interface CreateAccountPayload extends AccountPayload {
  domain: string;
}

interface AccountMutationResponse {
  account: WhitelistedDomain;
  accessToken: string;
  shareCode?: string;
}

const applyAccountMutation = (result: AccountMutationResponse) => {
  localStorage.setItem(TOKEN_STORAGE_KEY, result.accessToken);
  return result.account;
};

export const createAccount = async (
  payload: CreateAccountPayload,
): Promise<{ account: WhitelistedDomain; shareCode?: string }> => {
  const response = await api.post<AccountMutationResponse>('/admin/accounts', payload);
  return {
    account: applyAccountMutation(response.data),
    shareCode: response.data.shareCode,
  };
};

export const updateAccount = async (
  id: number,
  payload: AccountPayload,
): Promise<WhitelistedDomain> => {
  const response = await api.patch<AccountMutationResponse>(`/admin/accounts/${id}`, payload);
  return applyAccountMutation(response.data);
};

export const listDomains = async (): Promise<WhitelistedDomain[]> => {
  const basePath = DOMAINS_API_BASE_URL.endsWith('/api') ? '/admin/domains' : '/api/admin/domains';
  const response = await domainsApi.get<WhitelistedDomain[]>(basePath);
  return response.data;
};

export const upsertDomain = async (payload: {
  domain: string;
  name?: string;
  config?: Record<string, unknown>;
  includeGoldenDataset?: boolean;
}): Promise<WhitelistedDomain> => {
  const basePath = DOMAINS_API_BASE_URL.endsWith('/api') ? '/admin/domains' : '/api/admin/domains';
  const response = await domainsApi.post<WhitelistedDomain>(basePath, payload);
  return response.data;
};

export const deleteDomain = async (domain: string): Promise<{ message: string }> => {
  const basePath = DOMAINS_API_BASE_URL.endsWith('/api') ? '/admin/domains' : '/api/admin/domains';
  const response = await domainsApi.delete<{ message: string }>(basePath, { params: { domain } });
  return response.data;
};

export interface DomainHistoryEvent {
  id: number;
  actorEmail: string;
  action: string;
  details: Record<string, unknown> | null;
  createdAt: string;
}

export const getDomainHistory = async (domain: string): Promise<DomainHistoryEvent[]> => {
  const basePath = DOMAINS_API_BASE_URL.endsWith('/api') ? '/admin/domains/history' : '/api/admin/domains/history';
  const response = await domainsApi.get<DomainHistoryEvent[]>(basePath, { params: { domain } });
  return response.data;
};

export const createPipelineLogStream = (runId: string): EventSource => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  let url = `${base}/api/admin/pipeline/runs/${runId}/stream`;
  if (token) url += `?authorization=${encodeURIComponent(token)}`;
  return new EventSource(url);
};

// ==================== Batch Ingestion API ====================

export interface SearchDomain {
  id: number;
  key: string;
  label: string;
  isActive: boolean;
}

export interface IngestionBatch {
  id: number;
  batchName: string;
  domainId: number;
  adminId: number;
  talentIds: string[];
  profileCount: number;
  createdAt: string;
  domain?: SearchDomain;
}

export interface PoolProfile {
  id: string;
  name: string;
  batches: { id: number; name: string }[];
  tenants: { id: number; domains: number[] }[];
  domains: { key: string; label: string }[];
  lastUploadedAt: string | null;
  lastPublishedAt: string | null;
  lastUnpublishedAt: string | null;
  status: string | null;
  addedBy: string | null;
  uploadGroup: string | null;
  pipelineRunId: string | null;
  pipelineDomain: string | null;
  linkedinVerified: string | null;
  resumeVerification: string | null;
  publicVerification: string | null;
  agentRecommendation: string | null;
  enrichment: Record<string, unknown> | null;
}

export interface PoolProfilesResponse {
  profiles: PoolProfile[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const listPoolProfiles = async (params: {
  page?: number;
  pageSize?: number;
  batchId?: number;
  domainId?: number;
  tenantId?: number;
  publishedStatus?: 'published' | 'unpublished';
  status?: string;
  search?: string;
  sortCol?: string;
  sortDir?: 'asc' | 'desc';
}): Promise<PoolProfilesResponse> => {
  const response = await api.get<PoolProfilesResponse>('/admin/ingestion/profiles', { params });
  return response.data;
};

export const approveProfiles = async (ids: number[]): Promise<{ updated: number }> => {
  const response = await api.post<{ updated: number }>('/admin/pipeline/candidates/approve', { ids });
  return response.data;
};

export const rejectProfiles = async (ids: number[]): Promise<{ updated: number }> => {
  const response = await api.post<{ updated: number }>('/admin/pipeline/candidates/reject', { ids });
  return response.data;
};

export const moveToReview = async (ids: number[]): Promise<{ updated: number }> => {
  const response = await api.post<{ updated: number }>('/admin/pipeline/candidates/move-to-review', { ids });
  return response.data;
};

export const bulkEditCandidates = async (body: {
  selections: { talentId: string; currentDomain: string }[];
  newDomain?: string;
  newField?: string;
}): Promise<{ updated: number; skipped: { talentId: string; reason: string }[] }> => {
  const response = await api.patch<{ updated: number; skipped: { talentId: string; reason: string }[] }>(
    '/admin/pipeline/candidates/bulk-edit',
    body,
  );
  return response.data;
};

export const getFieldOptions = async (domain: string): Promise<string[]> => {
  const response = await api.get<string[]>('/admin/pipeline/field-options', { params: { domain } });
  return response.data;
};

// ==================== Pipeline Candidates API (MySQL-backed) ====================

export interface IdVerificationSub {
  workVerified: 'YES' | 'NO';
  eduVerified: 'YES' | 'NO';
  idVerified: 'YES' | 'NO';
  linkedinFound: 'YES' | 'NO';
  linkedinAccountUnder3y: 'YES' | 'NO';
  linkedinAccountOver3y: 'YES' | 'NO';
}

export interface PublicSourceCheckSub {
  educationVerification: 'YES' | 'NO' | 'NOT_FOUND';
  workVerification: 'YES' | 'NO' | 'NOT_FOUND';
  githubVerification: 'YES' | 'NO' | 'NOT_FOUND';
  disqualifiers: 'YES' | 'NO' | 'NOT_FOUND';
  agentVerdict: 'YES' | 'NO' | 'NOT_FOUND';
}

export interface PipelineCandidate {
  id: number;
  talentId: string;
  name: string;
  domain: string;
  field: string | null;
  status: string;
  linkedinVerified: string;
  linkedinVerifiedSub?: IdVerificationSub;
  resumeVerification: string;
  webAgentVerification: string;
  webAgentVerificationSub?: PublicSourceCheckSub;
  agentRecommendation: string;
  hourlyRate: number | null;
  uploadGroup: string;
  addedBy: string;
  addedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  batches: string[];
  enrichment: Record<string, unknown>;
  runId: string;
  reviewNotes: string | null;
  reviewTags: string[] | null;
  isGolden: boolean;
}

export const updateCandidateReview = async (
  id: number,
  data: { reviewNotes?: string | null; reviewTags?: string[] | null },
): Promise<{ updated: number }> => {
  const response = await api.patch<{ updated: number }>(`/admin/pipeline/candidates/${id}/review`, data);
  return response.data;
};

export const setCandidateGolden = async (
  talentId: string,
  isGolden: boolean,
  removal?: { reason: string; note?: string },
): Promise<{ success: boolean; talentId: string; isGolden: boolean }> => {
  const response = await api.patch<{ success: boolean; talentId: string; isGolden: boolean }>(
    `/admin/pipeline/candidates/${talentId}/golden`,
    { isGolden, ...(removal ?? {}) },
  );
  return response.data;
};

export const setCandidatesGoldenBulk = async (
  talentIds: string[],
  isGolden: boolean,
  removal?: { reason: string; note?: string },
): Promise<{ success: boolean; updated: number; isGolden: boolean }> => {
  const response = await api.patch<{ success: boolean; updated: number; isGolden: boolean }>(
    '/admin/pipeline/candidates/bulk-golden',
    { talentIds, isGolden, ...(removal ?? {}) },
  );
  return response.data;
};

export interface PipelineCandidatesResponse {
  candidates: PipelineCandidate[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  filterOptions?: {
    domains: string[];
    fields: string[];
    groups: string[];
    addedBys: string[];
    batches: string[];
    reviewTags?: string[];
  };
  summaryCounts?: {
    all: number;
    passed: number;
    needsReview: number;
  };
}

export const listPipelineCandidates = async (params: {
  status?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  sortCol?: string;
  sortDir?: 'asc' | 'desc';
  domain?: string;
  linkedinVerified?: string;
  resumeVerification?: string;
  webAgentVerification?: string;
  agentRecommendation?: string;
  reviewTags?: string;
  idvSubWorkVerified?: string;
  idvSubEduVerified?: string;
  idvSubIdVerified?: string;
  idvSubLinkedinFound?: string;
  idvSubAccountUnder3y?: string;
  idvSubAccountOver3y?: string;
  pscSubEducation?: string;
  pscSubWork?: string;
  pscSubGithub?: string;
  pscSubDisqualifiers?: string;
  pscSubAgentVerdict?: string;
  /** Prefer string[] (repeated ?field=). Comma-joined strings are not split — names may contain commas. */
  field?: string | string[];
  groupName?: string | string[];
  addedBy?: string | string[];
  batch?: string | string[];
  golden?: string;
  // Admin ES-filter panel fields (Task 7 — see AdminFilterSection.tsx)
  query?: string;
  companies?: string[];
  currentCompanies?: string[];
  pastCompanies?: string[];
  skills?: { name?: string }[];
  education?: string[];
  schools?: string[];
  countries?: string[];
  usState?: string[];
  yearOfExperience?: { min?: number; max?: number };
  hourlyRate?: { min?: number; max?: number };
  certificates?: string;
  researchPapersCount?: number;
  naturalLanguages?: string[];
  programmingLanguages?: string[];
  typeOfWork?: string;
  esField?: string;
  domainFilter?: string;
  hasGithubData?: boolean;
  titles?: string[];
  countriesExclude?: string[];
  companiesExclude?: string[];
  skillsExclude?: string[];
}): Promise<PipelineCandidatesResponse> => {
  // POST, not GET: a pasted talent-ID list is too long for the URL and trips HTTP 431.
  const response = await api.post<PipelineCandidatesResponse>('/admin/pipeline/candidates/search', params);
  return response.data;
};

// ==================== Profile Editor (admin Edit Profile modal) ====================

export interface ProfileEditorResponse {
  talentId: string;
  domain: string;
  esIndex: string;
  /** Raw ES doc; includes visibility flags (`hidden` per item, `*Hidden` per section). */
  profile: Record<string, any>;
}

/** Fetch the full ES doc for the admin Edit Profile modal (no visibility strip). */
export const getProfileEditor = async (talentId: string | number, domain: string): Promise<ProfileEditorResponse> => {
  const response = await api.get<ProfileEditorResponse>(
    `/admin/pipeline/profile-editor/${encodeURIComponent(String(talentId))}/${encodeURIComponent(domain.toLowerCase())}`,
  );
  return response.data;
};

/**
 * Persist edits + visibility toggles. Body should only contain editable fields:
 * title, yearOfExperience, field, country, tShortSummary, tSummary, tSkills, skills,
 * workExperience, education, projects, certifications, additionalVerifications,
 * hobbies, languages, linkedInProfile, googleScholarURL, teachingOrResearchProfile,
 * linkedInVerification, scholarVerify, and the top-level visibility flags
 * summaryHidden / skillsHidden / hobbiesHidden / verificationsHidden.
 */
export const saveProfileEditor = async (
  talentId: string | number,
  domain: string,
  edits: Record<string, any>,
): Promise<ProfileEditorResponse> => {
  const response = await api.put<ProfileEditorResponse>(
    `/admin/pipeline/profile-editor/${encodeURIComponent(String(talentId))}/${encodeURIComponent(domain.toLowerCase())}`,
    edits,
  );
  return response.data;
};

export interface LinkedinPreviewResponse {
  slug: string | null;
  verification: {
    Identity?: Array<{ Value?: string; Date?: string }>;
    Workplace?: Array<{ Value?: string; Date?: string }>;
    EducationalInstitution?: Array<{ Value?: string; Date?: string }>;
    joinedDate?: string | null;
  } | null;
  hasEvidence: boolean;
  summary: { identity: number; workplace: number; education: number; joinedDate: string | null } | null;
  error: string | null;
  apifyData?: any;
}

/**
 * Preview LinkedIn verification via Apify (read-only). Caller passes a URL;
 * backend extracts the slug, queries Apify (with Operia fallback), and returns
 * the resulting verification payload. Nothing is written to ES. The admin then
 * sends the verification object back along with the URL in saveProfileEditor.
 */
export const previewLinkedinVerification = async (
  talentId: string | number,
  domain: string,
  url: string,
): Promise<LinkedinPreviewResponse> => {
  const response = await api.post<LinkedinPreviewResponse>(
    `/admin/pipeline/profile-editor/${encodeURIComponent(String(talentId))}/${encodeURIComponent(domain.toLowerCase())}/linkedin-preview`,
    { url },
  );
  return response.data;
};

// ==================== Packets / Batches API ====================

export interface PacketSummary {
  id: number;
  name: string;
  company: string | null;
  status: string;
  createdAt: string;
  sentAt: string | null;
  openedAt: string | null;
  createdBy: string | null;
  sentBy: string | null;
  candidatesCount: number;
}

export interface PacketCandidateActionUser {
  firstName: string;
  at: string;
  actionType: 'Saved' | 'Viewed' | 'Discarded';
}

export interface PacketCandidate {
  talentId: string;
  name: string;
  domain: string;
  resultId: number;
  displayStatus: string;
  actionAt: string | null;
  actionUsers: PacketCandidateActionUser[];
}

export interface PacketHistoryEvent {
  userName: string;
  actionType: string;
  candidateName: string;
  at: string;
}

export interface PacketDetail extends PacketSummary {
  createdByName: string;
  candidates: PacketCandidate[];
  history: PacketHistoryEvent[];
}

export const listPackets = async (): Promise<PacketSummary[]> => {
  const response = await api.get<PacketSummary[]>('/admin/pipeline/packets');
  return response.data;
};

export const createPacket = async (name: string, company?: string): Promise<PacketSummary> => {
  const response = await api.post<PacketSummary>('/admin/pipeline/packets', { name, company });
  return response.data;
};

export const getPacketDetail = async (packetId: number): Promise<PacketDetail> => {
  const response = await api.get<PacketDetail>(`/admin/pipeline/packets/${packetId}`);
  return response.data;
};

export const sendPacket = async (packetId: number, tenantIds: number[], tenantNames: Record<number, string>): Promise<{ sentPacketIds: number[]; status: string }> => {
  const response = await api.post<{ sentPacketIds: number[]; status: string }>(`/admin/pipeline/packets/${packetId}/send`, { tenantIds, tenantNames });
  return response.data;
};

export const addToPacket = async (packetId: number, talentResultIds: number[]): Promise<{ added: number }> => {
  const response = await api.post<{ added: number }>(`/admin/pipeline/packets/${packetId}/candidates`, { talentResultIds });
  return response.data;
};

export const removeFromPacket = async (packetId: number, talentResultIds: number[]): Promise<{ removed: number }> => {
  const response = await api.post<{ removed: number }>(`/admin/pipeline/packets/${packetId}/candidates/remove`, { talentResultIds });
  return response.data;
};

export const renamePacket = async (packetId: number, name: string): Promise<{ id: number; name: string }> => {
  const response = await api.patch<{ id: number; name: string }>(`/admin/pipeline/packets/${packetId}`, { name });
  return response.data;
};

export const duplicatePacket = async (packetId: number): Promise<{ id: number; name: string; candidatesCopied: number }> => {
  const response = await api.post<{ id: number; name: string; candidatesCopied: number }>(`/admin/pipeline/packets/${packetId}/duplicate`);
  return response.data;
};

export const deletePacket = async (packetId: number): Promise<{ deleted: boolean }> => {
  const response = await api.delete<{ deleted: boolean }>(`/admin/pipeline/packets/${packetId}`);
  return response.data;
};

export interface IngestFromRunResult {
  success: boolean;
  indexed: number;
  failed: number;
  skippedPool: number;
  skippedInReview: number;
  total: number;
}

export const ingestFromPipelineRun = async (
  runId: string,
  domain: string,
  uploadGroup?: string,
): Promise<IngestFromRunResult> => {
  const response = await api.post<IngestFromRunResult>(
    '/admin/ingestion/ingest-from-run',
    { runId, domain, uploadGroup },
  );
  return response.data;
};

export const listSearchDomains = async (): Promise<SearchDomain[]> => {
  const response = await api.get<SearchDomain[]>('/admin/ingestion/domains');
  return response.data;
};

export const listAllSearchDomains = async (): Promise<SearchDomain[]> => {
  const response = await api.get<SearchDomain[]>('/admin/search-domains');
  return response.data;
};

export const createSearchDomain = async (key: string, label: string): Promise<SearchDomain> => {
  const response = await api.post<SearchDomain>('/admin/search-domains', { key, label });
  return response.data;
};

export const updateSearchDomain = async (
  id: number,
  data: { key?: string; label?: string; isActive?: boolean },
): Promise<SearchDomain> => {
  const response = await api.patch<SearchDomain>(`/admin/search-domains/${id}`, data);
  return response.data;
};

export const deleteSearchDomain = async (id: number): Promise<void> => {
  await api.delete(`/admin/search-domains/${id}`);
};

export const uploadBatch = async (
  domainId: number,
  file: File,
  batchName: string,
): Promise<IngestionResult & { batchId: number }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('batchName', batchName);
  formData.append('domainId', String(domainId));
  const response = await api.post<IngestionResult & { batchId: number }>(
    '/admin/ingestion/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 },
  );
  return response.data;
};

export const listBatches = async (domainId?: number): Promise<IngestionBatch[]> => {
  const params = domainId !== undefined ? { domainId } : {};
  const response = await api.get<IngestionBatch[]>('/admin/ingestion/batches', { params });
  return response.data;
};

export const getBatchDetail = async (batchId: number): Promise<IngestionBatch> => {
  const response = await api.get<IngestionBatch>(`/admin/ingestion/batches/${batchId}`);
  return response.data;
};

export const publishProfiles = async (payload: {
  batchId?: number;
  talentIds?: string[];
  tenantId: number;
  domainIds: number[];
  excludeTalentIds?: string[];
}): Promise<{ updated: number }> => {
  const response = await api.post<{ updated: number }>('/admin/ingestion/publish', payload);
  return response.data;
};

export const unpublishProfiles = async (
  talentIds: string[],
  tenantId: number,
  domainId: number,
): Promise<{ updated: number }> => {
  const response = await api.post<{ updated: number }>('/admin/ingestion/unpublish', {
    talentIds,
    tenantId,
    domainId,
  });
  return response.data;
};

export const removeProfiles = async (
  talentIds: string[],
): Promise<{ success: boolean; deleted: number; notFound: number; failed: number }> => {
  const response = await api.post<{ success: boolean; deleted: number; notFound: number; failed: number }>(
    '/admin/ingestion/remove',
    { talentIds },
  );
  return response.data;
};

// ==================== User Management API ====================

export interface AdminUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  role: 'user' | 'admin' | 'superadmin';
  allowedDomains: string[] | null;
  lastLoginTime: string | null;
  createdAt: string;
  updatedAt: string;
}

export const listUsers = async (params?: {
  search?: string;
  role?: string;
  status?: string;
}): Promise<{ users: AdminUser[]; total: number; callerRole: string }> => {
  const response = await api.get<{ users: AdminUser[]; total: number; callerRole: string }>('/admin/users', { params });
  return response.data;
};

export const updateUser = async (
  id: number,
  data: {
    role?: 'user' | 'admin';
    allowedDomains?: string[] | null;
    isActive?: boolean;
    firstName?: string;
    lastName?: string;
  },
): Promise<{ message: string }> => {
  const response = await api.patch<{ message: string }>(`/admin/users/${id}`, data);
  return response.data;
};

// ==================== Prompt Playground API ====================

export async function getPromptLabDefaults(): Promise<{ systemPrompt: string; userMessageTemplate: string }> {
  const res = await api.get<{ systemPrompt: string; userMessageTemplate: string }>('/admin/prompt-playground/defaults');
  return res.data;
}

export interface ParsedFraudResult {
  score: number;
  categoryScores: Record<string, number>;
  reasoning: string;
  redFlags: string[];
}

export interface TokenUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
}

export interface TalentResult {
  talentId: string;
  name: string;
  parsed?: ParsedFraudResult;
  latencyMs?: number;
  error?: string;
  resolvedUserMessage?: string;
  rawResponse?: string;
  variables?: { today: string; profileText: string };
  tokenUsage?: TokenUsage;
  model?: string;
}

export async function testFraudPrompt(body: {
  systemPrompt: string;
  userMessageTemplate: string;
  talentIds: string[];
  model: string;
}): Promise<TalentResult[]> {
  const res = await api.post<TalentResult[]>('/admin/prompt-playground/test', body);
  return res.data;
}

// ─── Skills Config ─────────────────────────────────────────────────────────

export interface SkillsConfigEntry {
  top: string[];
  all: string[];
}

export interface SkillsConfigResult {
  tenantId: number;
  domain: string;
  skillsConfig: Record<string, SkillsConfigEntry> | null;
}

export interface GeneratedSkillsResult {
  config: Record<string, SkillsConfigEntry>;
  counts: Record<string, Record<string, number>>;
}

const skillsBasePath = () =>
  DOMAINS_API_BASE_URL.endsWith('/api') ? '/admin/skills' : '/api/admin/skills';

export const getSkillsConfig = async (tenantId: number, domain: string): Promise<SkillsConfigResult> => {
  const res = await domainsApi.get<SkillsConfigResult>(skillsBasePath(), { params: { tenantId, domain } });
  return res.data;
};

export const saveSkillsConfig = async (
  tenantId: number,
  domain: string,
  skillsConfig: Record<string, SkillsConfigEntry>,
): Promise<{ tenantId: number; domain: string; fields: string[]; updated: boolean }> => {
  const res = await domainsApi.post(`${skillsBasePath()}/save`, { tenantId, domain, skillsConfig });
  return res.data;
};

export const generateSkillsForTenant = async (
  tenantId: number,
  domain: string,
): Promise<GeneratedSkillsResult> => {
  const res = await api.post<GeneratedSkillsResult>('/admin/pool-index/skills/generate', { tenantId, domain });
  return res.data;
};

export const getSkillsCounts = async (
  tenantId: number,
  domain: string,
): Promise<Record<string, Record<string, number>>> => {
  const res = await api.post('/admin/pool-index/skills/counts', { tenantId, domain });
  return res.data;
};

// ==================== Link Health API ====================

export interface LinkHealthCheck {
  id: number;
  talentId: string;
  linkType: string;
  linkIndex: number;
  url: string;
  status: string;
  reason: string | null;
  hidden: boolean;
  lastCheckedAt: string;
  talentName: string | null;
}

export interface LinkHealthSummary {
  total: number;
  ok: number;
  broken: number;
  replaced: number;
  hidden: number;
}

export interface LinkHealthStatus {
  isRunning: boolean;
  lastRunAt: string | null;
  lastRunStats: {
    checked: number;
    broken: number;
    replaced: number;
    hidden: number;
    unhidden: number;
    durationMs: number;
  } | null;
  lastError: string | null;
  progress: {
    totalLinks: number;
    completed: number;
    broken: number;
    phase: string;
  } | null;
}

export const listLinkHealthChecks = async (params: {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  linkType?: string;
  hidden?: string;
  sortCol?: string;
  sortDir?: 'asc' | 'desc';
}): Promise<{ data: LinkHealthCheck[]; total: number; page: number; pageSize: number }> => {
  const response = await api.get<{ data: LinkHealthCheck[]; total: number; page: number; pageSize: number }>(
    '/admin/link-health',
    { params },
  );
  return response.data;
};

export const getLinkHealthSummary = async (): Promise<LinkHealthSummary> => {
  const response = await api.get<LinkHealthSummary>('/admin/link-health/summary');
  return response.data;
};

export const getLinkHealthStatus = async (): Promise<LinkHealthStatus> => {
  const response = await api.get<LinkHealthStatus>('/admin/link-health/status');
  return response.data;
};

export const triggerLinkHealthScan = async (): Promise<{ started?: boolean; alreadyRunning?: boolean }> => {
  const response = await api.post<{ started?: boolean; alreadyRunning?: boolean }>('/admin/link-health/run-now');
  return response.data;
};

export const bulkSetLinkVisibility = async (ids: number[], hidden: boolean): Promise<{ updated: number }> => {
  const response = await api.patch<{ updated: number }>('/admin/link-health/bulk-visibility', { ids, hidden });
  return response.data;
};

export interface ScanChange {
  talentId: string;
  talentName: string;
  linkType: string;
  url: string;
  verdict: string;
  reason: string;
  action: string;
}

export const getLastRunChanges = async (): Promise<ScanChange[]> => {
  const response = await api.get<ScanChange[]>('/admin/link-health/last-run-changes');
  return response.data;
};

export const scanSelectedLinks = async (ids: number[]): Promise<{ started?: boolean; alreadyRunning?: boolean; count?: number }> => {
  const response = await api.post<{ started?: boolean; alreadyRunning?: boolean; count?: number }>('/admin/link-health/scan-selected', { ids });
  return response.data;
};

export const verifyLinkUrl = async (url: string, linkType?: string, talentName?: string, claimText?: string): Promise<{ verdict: string; reason: string; newUrl?: string | null }> => {
  const response = await api.post<{ verdict: string; reason: string; newUrl?: string | null }>('/admin/link-health/verify-url', { url, linkType, talentName, claimText });
  return response.data;
};

export interface TenantCoverage {
  id: number;
  name: string;
  domain: string;
  sections: { domainKey: string; field: string; count: number }[];
}

export interface DomainFieldCountsResponse {
  domainLabels: Record<string, string>;
  tenants: TenantCoverage[];
}

export interface CoverageTalent {
  talentId: string;
  name: string | null;
  field: string | null;
  status: string;
  sentAt: string | null;
}

export const getCoverageTalentIds = async (tenantName: string, domain: string, field?: string): Promise<{ talents: CoverageTalent[]; count: number }> => {
  const params: any = { tenantName, domain };
  if (field) params.field = field;
  const response = await api.get<{ talents: CoverageTalent[]; count: number }>('/admin/link-health/coverage-talent-ids', { params });
  return response.data;
};

export const getDomainFieldCounts = async (): Promise<DomainFieldCountsResponse> => {
  const response = await api.get<DomainFieldCountsResponse>('/admin/link-health/domain-field-counts');
  return response.data;
};

export default api;
